import React, { useState, useEffect, useMemo, useId } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ChevronLeft, DollarSign, AlertTriangle, TrendingUp, Calendar,
    Sparkles, CheckSquare, Lock, X, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import DisabledReason, { deniedReason } from '../components/DisabledReason';
import ContactSearchSelect from '../components/events/ContactSearchSelect';
import { usePermissions } from '../context/PermissionsContext';
import usePipelineDefinition from '../hooks/usePipelineDefinition';
import { dealStageMeta } from '../lib/dealStages';
import { getDealStageHistory } from '../lib/historyApi';
import { errorText } from '../lib/apiErrors';
import { personLabel } from '../lib/teammateLabels';
import { contactName } from '../lib/contactName';
import { followLink } from '../components/contacts/ContactEventsRail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NO_ROWS = [];
// The server's gate on the stakeholder routes; without it Add and Remove stay visible but disabled, with this as their title
// and as their accessible description. While permissions are still unknown they are disabled and say nothing.
const UPDATE_REASON = 'You do not have permission to update deals.';
// The DELETE route's gate (#477): the Delete action stays visible for everyone,
// disabled with this reason for a user without `deals:delete`.
const DELETE_REASON = 'You do not have permission to delete deals.';

export default function DealDetail() {
    const { dealId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const { hasEffectivePermission, permissionsUnknown } = usePermissions();
    const { definition: pipelineDefinition } = usePipelineDefinition('sales');
    const [deal, setDeal] = useState(null);
    const [account, setAccount] = useState(null);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [loadingAI, setLoadingAI] = useState(false);
    const [stageHistory, setStageHistory] = useState([]);
    const [stageHistoryError, setStageHistoryError] = useState('');
    // #906: the linked contacts, one row per contact. null until the first read answers.
    const [contacts, setContacts] = useState(null);
    const [contactsError, setContactsError] = useState('');
    const [contactsReload, setContactsReload] = useState(0);
    const [contactBusy, setContactBusy] = useState(false);
    // #477: the delete flow — dialog state, the typed confirmation, and the
    // not-found state a deleted (or unknown) deal's URL now shows.
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [dealNotFound, setDealNotFound] = useState(false);

    useEffect(() => {
        if (dealId) loadDeal();
    }, [dealId]);
    useEffect(() => {
        if (!dealId) return;
        getDealStageHistory(dealId, {}, getAuthHeader())
            .then(data => setStageHistory(Array.isArray(data) ? data : (data?.items || [])))
            .catch(err => setStageHistoryError(errorText(err, 'Failed to load stage history')));
    }, [dealId, getAuthHeader]);
    // React Router keeps this page mounted across /deals/:id: the previous deal's contacts must not show (with
    // live Remove buttons that would act on the NEW deal id) while this deal's own read is pending. A reload of
    // the same deal (`contactsReload`) keeps its rows on screen until the new answer. The same goes for #477's
    // not-found state: a deleted deal's page must not stain the next deal's URL.
    useEffect(() => { setContacts(null); setContactsError(''); setDealNotFound(false); }, [dealId]);
    useEffect(() => {
        if (!dealId) return undefined;
        let cancelled = false;
        axios.get(`${API}/deals/${dealId}/stakeholders`, { headers: getAuthHeader() })
            .then(res => { if (!cancelled) { setContacts(Array.isArray(res.data) ? res.data : NO_ROWS); setContactsError(''); } })
            .catch(err => {
                if (cancelled) return;
                setContacts(NO_ROWS);
                setContactsError(err?.response?.status === 403 ? "You don't have access to this deal's contacts." : errorText(err, 'Failed to load contacts'));
            });
        return () => { cancelled = true; };
    }, [dealId, getAuthHeader, contactsReload]);
    const linkedContactIds = useMemo(() => (contacts || NO_ROWS).map(r => r.contact_id).filter(Boolean), [contacts]);

    const loadDeal = async () => {
        try {
            const headers = getAuthHeader();
            const res = await axios.get(`${API}/deals/${dealId}`, { headers });
            setDeal(res.data);

            const [accountRes, activitiesRes] = await Promise.all([
                res.data.account_id ? axios.get(`${API}/accounts/${res.data.account_id}`, { headers }).catch(() => null) : null,
                axios.get(`${API}/activities?deal_id=${dealId}`, { headers }).catch(() => ({ data: [] })),
            ]);
            if (accountRes) setAccount(accountRes.data);
            setActivities(activitiesRes?.data || []);
        } catch (err) {
            // #477: a deleted deal answers 404 like an unknown one — the page
            // says so instead of rendering nothing behind a toast.
            if (err?.response?.status === 404) setDealNotFound(true);
            else toast.error('Failed to load deal');
        }
        finally { setLoading(false); }
    };

    const runAIAnalysis = async () => {
        setLoadingAI(true);
        try {
            const res = await axios.post(`${API}/ai/analyze-deal/${dealId}`, {}, { headers: getAuthHeader() });
            setAiAnalysis(res.data);
            setDeal(prev => ({ ...prev, ai_risk_score: res.data.risk_score, ai_insights: res.data.insights }));
            toast.success('AI analysis complete');
        } catch { toast.error('AI analysis failed'); }
        finally { setLoadingAI(false); }
    };

    const canEditContacts = hasEffectivePermission('deals:update');
    const editReason = deniedReason(canEditContacts, permissionsUnknown, UPDATE_REASON);
    const editReasonId = useId();
    const canDelete = hasEffectivePermission('deals:delete');
    const deleteReason = deniedReason(canDelete, permissionsUnknown, DELETE_REASON);
    const deleteReasonId = useId();
    // #477: the confirm button unlocks only for the deal's exact name as
    // displayed — case, spacing and all. Empty, partial and wrong-case stay
    // locked. A deal with no name (a webhook can create one) falls back to the
    // deal id, so the gate is never vacuously satisfied by an empty input.
    const expectedName = deal && (deal.title || dealId);
    const confirmMatches = deal && confirmText === expectedName;
    const confirmDelete = async () => {
        if (!confirmMatches || deleting) return;
        setDeleting(true);
        try {
            await axios.delete(`${API}/deals/${dealId}`, { headers: getAuthHeader() });
            toast.success('Deal deleted');
            navigate('/pipeline');
        } catch (err) { toast.error(errorText(err, 'Failed to delete deal')); }
        finally { setDeleting(false); }
    };
    const addContact = async (contactId) => {
        if (!contactId) return;
        setContactBusy(true);
        try {
            await axios.post(`${API}/deals/${dealId}/stakeholders`, { contact_id: contactId }, { headers: getAuthHeader() });
            toast.success('Contact added');
            setContactsReload(n => n + 1);
        } catch (err) { toast.error(errorText(err, 'Failed to add contact')); }
        finally { setContactBusy(false); }
    };
    const removeContact = async (row) => {
        if (!window.confirm(`Remove ${contactName(row.contact)} from this deal? The contact itself is not deleted.`)) return;
        setContactBusy(true);
        try {
            await axios.delete(`${API}/deals/${dealId}/contacts/${row.contact_id}`, { headers: getAuthHeader() });
            toast.success('Contact removed');
            setContactsReload(n => n + 1);
        } catch (err) { toast.error(errorText(err, 'Failed to remove contact')); }
        finally { setContactBusy(false); }
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;
    if (dealNotFound || !deal) return (
        <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-center" data-testid="deal-not-found">
            <h1 className="text-xl font-semibold text-stone-900">Deal not available</h1>
            <p className="text-sm text-stone-500">This deal may have been deleted, or you may not have access to it.</p>
            <Button variant="outline" onClick={() => navigate('/pipeline')}>Back to Pipeline</Button>
        </div>
    );

    const stageMeta = dealStageMeta(pipelineDefinition, deal.stage);
    const formatVal = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${v}`;

    return (
        <div className="space-y-6 max-w-5xl animate-fade-in">
            {/* Header */}
            <div className="flex items-start gap-4">
                <button onClick={() => navigate('/pipeline')} className="mt-1 p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600" data-testid="deal-back-btn">
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex-1">
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="deal-title">{deal.title}</h1>
                            <div className="flex items-center gap-3 mt-1">
                                <span className="text-sm text-stone-500">
                                    {account?.name || deal.account_name}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    stageMeta.outcome === 'won' ? 'bg-emerald-50 text-emerald-600' :
                                    stageMeta.outcome === 'lost' ? 'bg-red-50 text-red-600' :
                                    'bg-stone-100 text-stone-700'
                                }`}>
                                    {stageMeta.label}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button onClick={runAIAnalysis} disabled={loadingAI} className="ai-gradient text-white hover:opacity-90" data-testid="run-ai-analysis-btn">
                                <Sparkles className="w-4 h-4 mr-2" />
                                {loadingAI ? 'Analyzing...' : 'Run AI Analysis'}
                            </Button>
                            <DisabledReason id={deleteReasonId} reason={deleteReason} />
                            <Button onClick={() => { setDeleteOpen(true); setConfirmText(''); }} variant="outline"
                                disabled={!canDelete} title={deleteReason} aria-describedby={deleteReason ? deleteReasonId : undefined}
                                aria-label={`Delete ${deal.title}`} data-action="deal.delete" data-testid="deal-delete-btn"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-4 gap-4" data-testid="deal-metrics">
                {[
                    { label: 'Deal Value', value: formatVal(deal.value || 0), icon: DollarSign, color: 'text-emerald-600' },
                    { label: 'Risk Score', value: deal.ai_risk_score != null ? `${deal.ai_risk_score}%` : 'N/A', icon: AlertTriangle, color: (deal.ai_risk_score || 0) >= 70 ? 'text-red-600' : 'text-amber-600' },
                    { label: 'Win Probability', value: deal.probability != null ? `${deal.probability}%` : 'N/A', icon: TrendingUp, color: 'text-blue-600' },
                    { label: 'Close Date', value: deal.expected_close || 'TBD', icon: Calendar, color: 'text-violet-600' },
                ].map((m, i) => (
                    <div key={i} className="bg-white rounded-xl border border-stone-200 p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <m.icon className={`w-4 h-4 ${m.color}`} />
                            <p className="text-xs text-stone-400 font-medium">{m.label}</p>
                        </div>
                        <p className={`text-lg font-bold ${m.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{m.value}</p>
                    </div>
                ))}
            </div>

            {/* Deal Info + AI Insights */}
            <div className="grid grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Deal Information</h3>
                    <div className="space-y-3">
                        {[
                            { label: 'Company', value: account?.name || deal.account_name, link: account ? `/accounts/${account.account_id}` : null },
                            { label: 'Source', value: deal.source },
                            { label: 'Currency', value: deal.currency },
                        ].map(f => (
                            <div key={f.label} className="flex gap-4">
                                <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider w-24 flex-shrink-0 mt-0.5 capitalize">{f.label}</span>
                                {f.link ? (
                                    <button onClick={() => navigate(f.link)} className="text-sm text-stone-700 hover:text-stone-900">{f.value}</button>
                                ) : (
                                    <span className="text-sm text-stone-700 capitalize">{f.value || '—'}</span>
                                )}
                            </div>
                        ))}
                        {deal.description && (
                            <div>
                                <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider block mb-1">Description</span>
                                <p className="text-sm text-stone-600">{deal.description}</p>
                            </div>
                        )}
                        {(deal.tags || []).length > 0 && (
                            <div>
                                <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider block mb-1">Tags</span>
                                <div className="flex flex-wrap gap-1">
                                    {deal.tags.map(t => <span key={t} className="text-xs px-2 py-0.5 bg-stone-100 text-stone-700 rounded-full">{t}</span>)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-stone-200 p-6" style={{ borderTop: '3px solid transparent', borderImage: 'linear-gradient(135deg, #6366f1, #a855f7) 1' }}>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-6 h-6 ai-gradient rounded-md flex items-center justify-center">
                            <Sparkles className="w-3 h-3 text-white" />
                        </div>
                        <h3 className="font-semibold text-stone-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Insights</h3>
                    </div>
                    {deal.ai_insights ? (
                        <div className="text-sm text-stone-700 leading-relaxed whitespace-pre-line">{deal.ai_insights}</div>
                    ) : (
                        <div className="text-center py-4">
                            <Sparkles className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                            <p className="text-sm text-stone-400">Run AI analysis to get deal insights</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Contacts (#906): one row per linked contact; a contact the viewer cannot see is counted but not named */}
            <div className="bg-white rounded-xl border border-stone-200 p-6" data-testid="deal-contacts-card">
                <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Contacts{contacts ? ` (${contacts.length})` : ''}</h3>
                {/* One description for every Remove button; the Add box carries its own. */}
                <DisabledReason id={editReasonId} reason={editReason} />
                {contactsError ? (
                    <p className="text-sm text-stone-500" data-testid="deal-contacts-error">{contactsError}</p>
                ) : contacts === null ? (
                    <p className="text-sm text-stone-400">Loading contacts…</p>
                ) : contacts.length === 0 ? (
                    <p className="text-sm text-stone-400" data-testid="deal-contacts-empty">No contacts are linked to this deal.</p>
                ) : (
                    <ul className="divide-y divide-stone-100">
                        {contacts.map((row, i) => row.restricted ? (
                            <li key={`restricted-${i}`} className="flex items-center gap-2 py-3 text-sm text-stone-400" data-testid="deal-contact-restricted">
                                <Lock className="w-3.5 h-3.5" /> A contact you can't see
                            </li>
                        ) : (
                            <li key={row.contact_id} className="flex items-start justify-between gap-4 py-3" data-testid={`deal-contact-${row.contact_id}`}>
                                <div className="min-w-0">
                                    <a href={`/contacts/${row.contact_id}`} onClick={e => followLink(e, `/contacts/${row.contact_id}`, navigate)}
                                        className="text-sm font-medium text-stone-900 hover:underline" data-testid={`deal-contact-link-${row.contact_id}`}>
                                        {contactName(row.contact)}
                                    </a>
                                    {row.contact?.title && <p className="text-xs text-stone-500">{row.contact.title}</p>}
                                    <p className="text-xs text-stone-500">Contact Owner: {personLabel(row.contact_owner_id, row.contact_owner_name)
                                        || <span className="text-stone-300" title="No Contact Owner">—</span>}</p>
                                </div>
                                <button type="button" onClick={() => removeContact(row)} disabled={contactBusy || !canEditContacts} title={editReason}
                                    aria-describedby={editReason ? editReasonId : undefined}
                                    aria-label={`Remove ${contactName(row.contact)} from this deal`} data-testid={`deal-contact-remove-${row.contact_id}`}
                                    className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-stone-400">
                                    <X className="w-4 h-4" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                {!contactsError && contacts !== null && (
                    <div className="mt-4 max-w-sm" data-testid="deal-contact-add">
                        <ContactSearchSelect value="" onChange={contactId => addContact(contactId)} excludeIds={linkedContactIds}
                            placeholder="Add a contact…" emptyText="No matching contacts" disabled={!canEditContacts} disabledReason={editReason} />
                    </div>
                )}
            </div>

            {/* Activities */}
            <div className="bg-white rounded-xl border border-stone-200 p-6" data-testid="deal-stage-history">
                <h3 className="font-semibold text-stone-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Stage history</h3>
                {stageHistoryError ? <p className="text-sm text-red-600">{stageHistoryError}</p> : stageHistory.length === 0 ? <p className="text-sm text-stone-400">No stage changes recorded.</p> : <div className="space-y-2">{stageHistory.map((item, i) => <div key={item.event_id || item.id || i} className="text-sm text-stone-600">{item.old_label || item.from_label || item.old_value || item.from_stage || '—'} → {item.new_label || item.to_label || item.new_value || item.to_stage || item.stage || '—'} · {item.actor?.name || item.actor_name || item.user_name || 'Unknown'} · {item.changed_at || item.created_at || item.timestamp || ''}</div>)}</div>}
            </div>


            <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Activities ({activities.length})</h3>
                {activities.length === 0 ? (
                    <div className="text-center py-6 text-stone-400">
                        <CheckSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No activities logged for this deal</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {activities.map(a => (
                            <div key={a.activity_id} className="flex items-start gap-3 p-3 rounded-lg bg-stone-50 border border-stone-100">
                                <div className="w-7 h-7 rounded-full bg-white border border-stone-200 flex items-center justify-center text-sm flex-shrink-0">
                                    {a.type === 'email' ? <span>E</span> : a.type === 'call' ? <span>C</span> : a.type === 'meeting' ? <span>M</span> : <span>N</span>}
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-stone-900">{a.title}</p>
                                    <p className="text-xs text-stone-400">{a.description || a.type}</p>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>{a.status}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Delete confirmation (#477): names the deal and unlocks only for its exact name. */}
            <Dialog open={deleteOpen} onOpenChange={open => { setDeleteOpen(open); if (!open) setConfirmText(''); }}>
                <DialogContent data-testid="deal-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete “{deal.title}”?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-stone-600">
                        This removes the deal from your pipeline, lists and search. An administrator can restore it later.
                    </p>
                    <p className="text-sm text-stone-600">
                        Type <span className="font-semibold text-stone-900" data-testid="deal-delete-expected-name">{expectedName}</span> to confirm.
                    </p>
                    <input
                        value={confirmText}
                        onChange={e => setConfirmText(e.target.value)}
                        aria-label="Deal name"
                        data-testid="deal-delete-confirm-input"
                        className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
                        autoComplete="off"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteOpen(false)} data-testid="deal-delete-cancel">Cancel</Button>
                        <Button onClick={confirmDelete} disabled={!confirmMatches || deleting} data-testid="deal-delete-confirm"
                            className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">
                            {deleting ? 'Deleting…' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
